<template>
  <div class="iq-card iq-card-sm">
    <div class="card-body">
      <!-- ================= TOTALES (estimados) ================= -->
      <h5 class="mb-3">{{ $t('pos.lbl_totals') }}</h5>

      <dl class="pos-totals mb-0">
        <dt>{{ $t('pos.lbl_subtotal_services') }}</dt>
        <dd>{{ money(store.servicesSubtotal) }}</dd>
        <dt>{{ $t('pos.lbl_subtotal_products') }}</dt>
        <dd>{{ money(store.productsSubtotal) }}</dd>
        <dt class="pos-totals-strong">{{ $t('pos.lbl_subtotal') }}</dt>
        <dd class="pos-totals-strong">{{ money(store.subtotal) }}</dd>
        <template v-if="store.manualDiscountAmount > 0">
          <dt class="text-success">{{ $t('pos.lbl_discount') }}<template v-if="store.discount_type === 'percent'"> ({{ store.discount_value }}%)</template></dt>
          <dd class="text-success">- {{ money(store.manualDiscountAmount) }}</dd>
        </template>
        <dt>{{ $t('pos.lbl_tax') }}</dt>
        <dd>{{ money(store.taxAmount) }}</dd>
        <dt class="pos-totals-total">{{ $t('pos.lbl_total') }} <small class="text-muted">({{ $t('pos.lbl_estimated') }})</small></dt>
        <dd class="pos-totals-total">{{ money(store.estimatedTotal) }}</dd>
      </dl>

      <AlertMessage class="mt-3 mb-0" variant="info" v-if="store.hasServerResolvedAmounts" :message="$t('pos.lbl_estimate_notice')" />

      <!-- ================= ACCIONES (botones -> modales) ================= -->
      <div class="pos-actions mt-3">
        <button type="button" class="pos-action pos-tap" :class="{ active: hasDiscount }" @click="showDiscount = true">
          <i class="fa-solid fa-tag" aria-hidden="true"></i>
          <span>{{ $t('pos.lbl_discounts') }}</span>
          <span class="pos-action-badge" v-if="discountBadge">{{ discountBadge }}</span>
        </button>
        <button type="button" class="pos-action pos-tap" :class="{ active: tenderCount > 0 }" @click="showTenders = true">
          <i class="fa-solid fa-coins" aria-hidden="true"></i>
          <span>{{ $t('pos.lbl_tenders') }}</span>
          <span class="pos-action-badge" v-if="tenderCount > 0">{{ tenderCount }}</span>
        </button>
        <!-- COBROS: una sola opcion para pago simple o multiple. -->
        <button type="button" class="pos-action pos-action-pay pos-tap" @click="showPayments = true">
          <i class="fa-solid fa-cash-register" aria-hidden="true"></i>
          <span>{{ $t('pos.lbl_payments') }}</span>
          <span class="pos-action-badge" v-if="paymentBadge">{{ paymentBadge }}</span>
        </button>
      </div>

      <!-- ATAJO DE EFECTIVO: importe pagado + vuelto en linea (caso comun, sin abrir
           el modal). Solo en pago simple con efectivo. Escribe en la linea de pago. -->
      <div class="pos-cash-quickpay mt-3" v-if="isCashQuickPay">
        <label class="form-label small mb-1">{{ $t('pos.lbl_cash_received') }}</label>
        <div class="input-group input-group-sm mb-2">
          <span class="input-group-text">{{ currencySymbol }}</span>
          <input type="number" min="0" step="0.01" class="form-control" :placeholder="money(store.estimatedTotal)" v-model="cashReceived" />
          <button type="button" class="btn btn-outline-primary pos-tap" @click="cashReceived = store.estimatedTotal">{{ $t('pos.lbl_exact') }}</button>
        </div>
        <div class="pos-cash-quick mb-2">
          <button type="button" class="btn btn-sm btn-outline-secondary pos-tap" v-for="a in quickAmounts" :key="`qp-${a}`" @click="addCash(a)">+{{ money(a) }}</button>
          <button type="button" class="btn btn-sm btn-outline-danger pos-tap" @click="cashReceived = null" :aria-label="$t('pos.lbl_clear')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
        </div>
        <div class="pos-change" :class="store.cashShortfall > 0 ? 'pos-change-short' : 'pos-change-ok'" v-if="Number(cashReceived) > 0">
          <span>{{ store.cashShortfall > 0 ? $t('pos.lbl_missing') : $t('pos.lbl_change') }}</span>
          <strong>{{ money(store.cashShortfall > 0 ? store.cashShortfall : store.changeDue) }}</strong>
        </div>
      </div>

      <hr />

      <AlertMessage class="mt-3 mb-0" :message="store.error || ''" :list="store.errorList" :title="$t('pos.lbl_error')" />
      <AlertMessage class="mt-3 mb-0" variant="warning" v-if="blockingMessage" :message="blockingMessage" />

      <button type="button" class="btn btn-primary w-100 mt-3 pos-charge pos-tap" v-if="puedeCobrar" :disabled="!canCharge" @click="emit('charge')">
        <span v-if="store.submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ store.submitting ? $t('pos.lbl_charging') : `${$t('pos.lbl_charge')} · ${money(store.estimatedTotal)}` }}
      </button>

      <!-- «Enviar a caja»: quien no cobra arma la venta y la deja abierta para la encargada. -->
      <p class="small text-muted mt-3 mb-0" v-if="!puedeCobrar && puedeEnviar">{{ $t('pos.lbl_send_only_info') }}</p>
      <button type="button" class="btn w-100 pos-tap" :class="puedeCobrar ? 'btn-outline-secondary btn-sm mt-2' : 'btn-primary mt-2 pos-charge'" v-if="puedeEnviar" :disabled="!canSend" @click="emit('send')">
        <span v-if="store.submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        <i v-else class="fa-solid fa-paper-plane me-1" aria-hidden="true"></i>
        {{ store.submitting ? $t('pos.lbl_sending') : `${$t('pos.lbl_send_to_cashier')} · ${money(store.estimatedTotal)}` }}
      </button>

      <!-- ================= MODALES ================= -->
      <!-- Descuento (manual % / importe) + cupon: excluyentes. -->
      <PosModal v-model="showDiscount" :title="$t('pos.lbl_discounts')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_cancel')">
        <p class="text-muted small mb-2">{{ $t('pos.lbl_discounts_help') }}</p>

        <label class="form-label small mb-1">{{ $t('pos.lbl_manual_discount') }}</label>
        <div class="input-group input-group-sm mb-2">
          <button type="button" class="btn" :class="store.discount_type === 'percent' ? 'btn-primary' : 'btn-outline-secondary'" @click="setDiscountType('percent')">%</button>
          <button type="button" class="btn" :class="store.discount_type === 'fixed' ? 'btn-primary' : 'btn-outline-secondary'" @click="setDiscountType('fixed')">{{ currencySymbol }}</button>
          <input type="number" min="0" step="0.01" class="form-control" :placeholder="store.discount_type === 'percent' ? '0 - 100' : '0.00'" :disabled="!store.discount_type" v-model="store.discount_value" />
          <button type="button" class="btn btn-outline-danger" v-if="store.discount_type" @click="clearDiscount" :aria-label="$t('pos.lbl_clear')">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
        <small class="text-danger d-block mb-2" v-if="store.coupon_code && store.discount_type">{{ $t('pos.lbl_discount_coupon_conflict') }}</small>

        <label class="form-label small mb-1" for="pos-coupon">{{ $t('pos.lbl_coupon') }}</label>
        <input id="pos-coupon" type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_coupon_ph')" v-model.trim="store.coupon_code" />
        <p class="text-muted small mt-2 mb-0"><i class="fa-solid fa-circle-info me-1" aria-hidden="true"></i>{{ $t('pos.lbl_package_pending') }}</p>
      </PosModal>

      <!-- Pagado con: tenders (puntos / monedero / gift card). No reducen el IVA. -->
      <PosModal v-model="showTenders" :title="$t('pos.lbl_tenders')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_cancel')">
        <p class="text-muted small mb-2">{{ $t('pos.lbl_tenders_help') }}</p>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label small mb-1" for="pos-points">{{ $t('pos.lbl_points') }}</label>
            <input id="pos-points" type="number" min="1" step="1" class="form-control form-control-sm" :placeholder="$t('pos.lbl_points_ph')" v-model="store.use_points" />
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1" for="pos-wallet">{{ $t('pos.lbl_wallet') }}</label>
            <input id="pos-wallet" type="number" min="0" step="0.01" class="form-control form-control-sm" :placeholder="$t('pos.lbl_wallet_ph')" v-model="store.use_wallet" />
          </div>
          <div class="col-12">
            <label class="form-label small mb-1" for="pos-giftcard">{{ $t('pos.lbl_gift_card') }}</label>
            <input id="pos-giftcard" type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_gift_card_ph')" v-model.trim="store.gift_card_code" />
          </div>
        </div>
        <p class="text-muted small mt-2 mb-0">{{ $t('pos.lbl_server_resolves') }}</p>
      </PosModal>


      <!-- COBROS: modal unificado (pago simple o multiple, con efectivo/cambio y pasarela). -->
      <PaymentsModal v-model="showPayments" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import PosModal from './PosModal.vue'
import PaymentsModal from './PaymentsModal.vue'
import { usePos } from '../../store/pos'
import { money } from '../money'
import { PAYMENT_METHODS } from '../constants/pos'

const store = usePos()
const { t } = useI18n()
const emit = defineEmits(['charge', 'send'])

// --- Modales ---
const showDiscount = ref(false)
const showTenders = ref(false)
const showPayments = ref(false)

const isOnline = (method) => {
  const found = PAYMENT_METHODS.find((m) => m.value === method)
  return !!found && !found.offline
}

// Badge del boton Cobros: metodo (pago simple) o nº de medios (multiple).
const paymentBadge = computed(() => {
  if (store.isMultiPayment) return String(store.payment_lines.length)
  const found = PAYMENT_METHODS.find((m) => m.value === store.payment_lines[0]?.method)
  return found ? t(found.label) : ''
})

// Indicadores para los botones de accion.
const hasDiscount = computed(() => !!store.coupon_code || (!!store.discount_type && Number(store.discount_value) > 0))
const discountBadge = computed(() => {
  if (store.manualDiscountAmount > 0) return store.discount_type === 'percent' ? `${store.discount_value}%` : money(store.manualDiscountAmount)
  if (store.coupon_code) return store.coupon_code
  return ''
})
const tenderCount = computed(
  () => (Number(store.use_points) > 0 ? 1 : 0) + (Number(store.use_wallet) > 0 ? 1 : 0) + (store.gift_card_code ? 1 : 0)
)

const currencySymbol = (typeof window !== 'undefined' && window.defaultCurrencySymbol) || '$'
const quickAmounts = [5, 10, 20, 50]

// Atajo de efectivo (caso comun): escribe en la primera linea de pago.
const isCashQuickPay = computed(
  () => store.isSinglePayment && store.payment_lines[0]?.method === 'cash'
)
const cashReceived = computed({
  get: () => store.payment_lines[0]?.received,
  set: (v) => {
    if (store.payment_lines[0]) store.payment_lines[0].received = v === '' || v === null ? null : Number(v)
  }
})
const addCash = (amount) => {
  const base = Number(cashReceived.value) || 0
  cashReceived.value = Math.round((base + amount) * 100) / 100
}

// --- Descuento manual ---
const setDiscountType = (type) => {
  store.discount_type = store.discount_type === type ? '' : type
  if (!store.discount_type) store.discount_value = null
}

const clearDiscount = () => {
  store.discount_type = ''
  store.discount_value = null
}

const blockingReason = computed(() => {
  if (!store.customer) return t('pos.lbl_customer_required')
  if (!store.hasLines) return t('pos.lbl_empty_cart')
  if (store.services.some((line) => !line.employee_id)) return t('pos.lbl_employee_required')
  if (store.coupon_code && store.discount_type && Number(store.discount_value) > 0) return t('pos.lbl_discount_coupon_conflict')

  // Pago MULTIPLE: cuadre exacto, sin tenders server-side, y cada medio online
  // con su referencia (mismo contrato del backend).
  if (store.isMultiPayment) {
    if (store.hasServerResolvedAmounts) return t('pos.lbl_split_no_tenders')
    if (store.payment_lines.some((p) => isOnline(p.method) && !String(p.reference || '').trim())) {
      return t('pos.lbl_split_ref_required')
    }
    if (!store.paymentsBalanced) return t('pos.lbl_split_unbalanced')
    return ''
  }

  // Pago SIMPLE online: exige referencia (en Datafast la pone el widget al volver).
  if (store.effectiveMethodIsOnline && !String(store.payment_lines[0]?.reference || '').trim()) {
    return t('pos.lbl_reference_required')
  }
  return ''
})

// Mensaje de bloqueo VISIBLE: se omite el de "cliente requerido" (el selector de
// cliente esta arriba y el boton Cobrar queda deshabilitado igual).
const blockingMessage = computed(() => {
  const r = blockingReason.value
  return r === t('pos.lbl_customer_required') ? '' : r
})

const canCharge = computed(() => !store.submitting && !blockingReason.value && !!store.branchId)

// Permisos que la vista Blade deja en window (sin ellos se asume el comportamiento previo: cobrar).
const permisos = window.POS_PERMISOS || {}
const puedeCobrar = permisos.cobrar !== false
const puedeEnviar = permisos.enviar === true
// Enviar a caja no exige nada del pago: solo clienta, lineas y empleado por servicio.
const canSend = computed(() =>
  !store.submitting && !!store.branchId && !!store.customer && store.hasLines &&
  !store.services.some((line) => !line.employee_id)
)
</script>
